import { FieldValues, Path, PathValue, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { SelectInputProps } from './Select.types';

export default function SelectInput<T, C extends FieldValues = FieldValues>({
  control,
  name,
  gridSize = 6,
  fullWidth = true,
  variant = 'outlined',
  label,
  shrink = true,
  defaultValue = '',
  options = [],
  getOptionLabel,
  getOptionValue = (option) => option,
  readOnly = false,
  size = 'small',
  addNoneOption = true,
  disabled = false,
  isLoading = false,
  ...props
}: SelectInputProps<T, C>) {
  const {
    field: { ref, value, onBlur, ...inputProps },
    fieldState: { error },
  } = useController({
    name: name as Path<C>,
    control,
    defaultValue: defaultValue as PathValue<C, Path<C>>,
  });
  const { t } = useTranslation();

  return (
    <Grid item xs={gridSize}>
      <FormControl variant={variant} error={!!error} fullWidth={fullWidth} size={size}>
        <InputLabel shrink={shrink} id="label">
          {/* Label shrinked by default */}
          {label}
        </InputLabel>
        <Select
          labelId="label"
          onBlur={onBlur}
          label={label}
          value={value}
          notched /* Space for the label */
          renderValue={(value: T) => getOptionLabel(value)} /* Necessary to display default value */
          inputProps={{
            readOnly,
            ...inputProps,
            ref,
            name,
            disabled,
          }}
          disabled={disabled || isLoading}
          endAdornment={isLoading ? <CircularProgress color="inherit" size={20} /> : null}
          {...props}
        >
          {/* Default null value */}
          {addNoneOption && (
            <MenuItem value={''}>
              <em>None</em>
            </MenuItem>
          )}
          {/* Map rest of the options from props */}
          {options.map((option, idx) => {
            return (
              <MenuItem key={idx} value={getOptionValue(option) as string | number | readonly string[] | undefined}>
                {getOptionLabel(option)}
              </MenuItem>
            );
          })}
        </Select>

        <FormHelperText>{error && t(error.message as string)}</FormHelperText>
      </FormControl>
    </Grid>
  );
}
